import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { PriceService } from '@core/services/price/price.service';
import { environment } from '@environment';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { ToastrService } from '@core/services/notification/toastr.service';
import { PonchoUIModule } from '@poncho/ui';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { DeferImageLoadDirective } from '../../../directives/defer-image-load.directive';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { FeatureToggleDirective } from '../../../directives/feature-toggle.directive';
import { CustomizationData, DiscountOffer, CtGroups, CustomizationTypeDetail } from '@shared/models/customization';

@Component({
    selector: 'group-customization',
    templateUrl: './group-customization.component.html',
    styleUrls: ['./group-customization.component.scss'],
    standalone: true,
    imports: [
        FeatureToggleDirective,
        MatDialogModule,
        MatIconModule,
        NgClass,
        DeferImageLoadDirective,
        MatButtonModule,
        MatRadioModule,
        MatCheckboxModule,
        NgTemplateOutlet,
        PonchoUIModule
    ]
})
export class GroupCustomizationComponent implements OnInit {
    @Input() product;
    @Input() menuData;
    @Input() currentCustomization;

    @Output() closeDialog = new EventEmitter<string>();
    @Output() submitCart = new EventEmitter<Object>();

    @ViewChild('scrollContainer', { read: ElementRef }) public scrollContainer: ElementRef<any>;
    @ViewChild('scrollContent') scrollContent: ElementRef<any>;
    @ViewChild('customizationContainer') customizationContainer: ElementRef<any>;

    DEPLOY_URL: string;
    customizationList = new Map<string, CustomizationTypeDetail[]>();
    customizationGroupIds: string[] = [];
    selectedCustomization: CustomizationData = {};
    activeGroupId: any;
    buttonCount = {
        total: 0,
        selected: 0
    };
    totalPrice = 0;
    ct_groups: CtGroups;
    brand_id: number;
    defaultPrice = 0;
    horizontalScroll: any;
    verticalScroll: any;
    productConfig: DiscountOffer[];
    bgSequence: any = [];
    bgInfo: any = {};
    bottomStrip: any = [];
    lottieUrl: string;
    filteredCustomizations = {};
    groupBaseIcon = '';

    constructor(
        private priceService: PriceService,
        private toastrService: ToastrService
    ) {
        this.DEPLOY_URL = environment.DEPLOY_URL;
        this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/confetti`;
    }

    ngOnInit(): void {
        const brand_id = this.menuData.brand_id;
        this.groupBaseIcon = `${environment.assetsUrl}/icon/xhdpi/customization_type_groups/${brand_id}`;
        this.ct_groups = this.menuData.ct_groups;
        this.productConfig = this.menuData.product_t_config[this.product.t] || [];
        this.setSequence();
        this.createModel();
        this.defaultPrice = this.getProductSizePrice(this.menuData.product_sizes[this.product.product_sizes[0].id]);
        this.horizontalScroll = this.onScroll.bind(this);
        this.verticalScroll = this.onVerticalScroll.bind(this);
    }

    setSequence() {
        const productCustomisationTypes = this.product.product_sizes[0].product_customization_types;
        for (const custType of productCustomisationTypes) {
            custType.sequence = this.ct_groups[custType.ct_group_id].s;
            custType.customizations = custType.customizations.sort((first, second) => first.sequence - second.sequence);
            custType.filter_opts = custType.filter_opts || [];
        }
        productCustomisationTypes.sort((first, second) => first.sequence - second.sequence);
    }

    createModel() {
        this.createCustomizationList();
        this.customizationGroupIds = [...this.customizationList.keys()];
        this.onGroupChange(this.customizationGroupIds[0]);
        this.getProductInfo();
    }

    createCustomizationList() {
        const product_customization_types = this.product.product_sizes[0].product_customization_types;
        for (let customizationType of product_customization_types) {
            customizationType = {
                ...customizationType,
                customizations: customizationType.customizations.filter(data => !data.sold_out)
            };
            if (!this.customizationList.has(customizationType.ct_group_id)) {
                this.customizationList.set(customizationType.ct_group_id, [customizationType]);
            } else {
                this.customizationList.get(customizationType.ct_group_id).push(customizationType);
            }
            this.updateCustomization(customizationType.filter_opts[0], customizationType.id, customizationType);
        }
    }

    onScroll() {
        if (this.scrollContainer && this.scrollContainer.nativeElement) {
            const index = Array.from(this.scrollContainer.nativeElement.children).findIndex(val => {
                return val['children'][0].classList[1] === 'active';
            });
            if (index != -1) {
                const indexEle = this.scrollContainer.nativeElement.children[index];
                indexEle.scrollIntoView(true);
            }
        }
    }

    onVerticalScroll() {
        this.customizationContainer.nativeElement.firstChild.scrollIntoView(true);
    }

    //to create product customization types list to keep track of user input
    getProductInfo() {
        const product_customization_types = this.product.product_sizes[0].product_customization_types;
        for (const customization_type of product_customization_types) {
            if (this.selectedCustomization[customization_type.ct_group_id] === undefined) {
                this.selectedCustomization[customization_type.ct_group_id] = {
                    customization_types: {},
                    label: '',
                    valid: false,
                    price: 0,
                    group_id: customization_type.ct_group_id
                };

                this.buttonCount['total']++;
            }

            // for cart item customisation
            const currentSelection = this.currentCustomization[customization_type['id']];
            if (currentSelection) {
                const customizationList = customization_type.customizations;
                const labels = [];
                let totalPrice = 0;

                currentSelection.forEach((custId: number) => {
                    const cust = customizationList.find(c => c.id === custId);
                    labels.push(cust.label);
                    totalPrice += this.getProductSizePrice(cust);
                });

                this.selectedCustomization[customization_type.ct_group_id].customization_types[customization_type['id']] = {
                    label: labels,
                    id: currentSelection,
                    filter: customization_type.filter_opts[0],
                    price: totalPrice,
                    min: customization_type.min,
                    max: customization_type.max
                };
            } else {
                this.selectedCustomization[customization_type.ct_group_id].customization_types[customization_type['id']] = {
                    label: [],
                    id: [],
                    filter: customization_type.filter_opts[0],
                    price: 0,
                    min: customization_type.min,
                    max: customization_type.max
                };

                if (!Object.entries(this.currentCustomization).length) {
                    this.defaultCustomization(customization_type);
                }
            }
        }
        this.isSelectionComplete();
    }

    defaultCustomization(customization_type) {
        const preSelectedCust =
            this.selectedCustomization[customization_type.ct_group_id].customization_types[customization_type['id']];
        const customizations = customization_type.customizations;
        for (const cust of customizations) {
            if (cust.dflt && !cust.sold_out) {
                preSelectedCust.id.push(cust.id)
                preSelectedCust.label.push(cust.label);
                preSelectedCust.price += cust.price;
            }
        }
    }

    getProductSizePrice(productSize) {
        return this.priceService.getProductSizePrice(productSize).price;
    }

    // to close dialog box
    close() {
        this.closeDialog.emit();
    }

    onGroupChange(id) {
        this.activeGroupId = id;
        setTimeout(this.horizontalScroll, 0);
        setTimeout(this.verticalScroll, 0);
    }

    //called on radio button selection
    toggleProductSize(data, custType) {
        const value = data.value || data.source.value;
        const item = this.selectedCustomization[custType.ct_group_id].customization_types[custType.id];
        const isChecked = data.source._checked;

        if (isChecked && item.max === item.id.length) {
            if (data && data.source instanceof MatCheckbox) {
                data.source.checked = false;
                this.toastrService.openSnackBar(
                    `User selects more than Max - Oops! You can pick only ${item.max} items from ${custType.label}`,
                    'OKAY'
                );
                return;
            }
        }

        if (isChecked) {
            this.addCustomization(item, value);
        } else {
            this.removeCustomization(item, value);
        }

        this.isSelectionComplete();
    }

    addCustomization(item: any, value: any): void {
        const { min, max } = item;

        if (min === max && max === 1) {
            item.label = [value.label];
            item.price = value.price;
            item.id = [value.id];
        } else {
            item.label.push(value.label);
            item.price += value.price;
            item.id.push(value.id);
        }
    }

    removeCustomization(item: any, value: any): void {
        const labelIndex = item.label.indexOf(value.label);
        if (labelIndex > -1) {
            item.label.splice(labelIndex, 1);
        }

        const idIndex = item.id.indexOf(value.id);
        if (idIndex > -1) {
            item.id.splice(idIndex, 1);
        }
    }

    validateGroupAndHydrateInfo(group) {
        const groupInfo = this.selectedCustomization[group];
        groupInfo.valid = false;
        groupInfo['label'] = this.ct_groups[group].l;
        const customizationTypes = groupInfo.customization_types;

        let allSelected = true;
        const groupLabel = [];
        let groupPrice = 0;

        for (const custType in customizationTypes) {
            const { id, label, price, min } = customizationTypes[custType];
            if (min > id.length) {
                allSelected = false;
            } else {
                groupLabel.push(...label);
                groupPrice += price;
                this.totalPrice += price;
            }
        }
        groupInfo.price = groupPrice;
        if (allSelected) {
            groupInfo['label'] = groupLabel.join(', ') || groupInfo['label'];
            groupInfo.valid = true;
            this.buttonCount['selected']++;
        }
    }

    //checks if all customization under a group is complete
    isSelectionComplete() {
        this.buttonCount['selected'] = 0;
        this.totalPrice = 0;
        for (const group in this.selectedCustomization) {
            this.validateGroupAndHydrateInfo(group);
        }
        this.bgCalculation();
    }

    bgCalculation() {
        const bgSequence = [];
        this.bgInfo = {};
        let bottomStrip = '';

        if (this.buttonCount['total'] - this.buttonCount['selected'] === 0) {
            for (const group in this.selectedCustomization) {
                bgSequence.push(this.selectedCustomization[group]);
            }
            bgSequence.sort(function (a, b) {
                return b.price - a.price;
            });

            for (const config of this.productConfig) {
                this.bgInfo[bgSequence[config.seq - 1].group_id] = config;
                for (const group in this.bgInfo) {
                    let discount_amount = 0;

                    const { d_type, d_val, seq, d_text } = this.bgInfo[group];
                    if (d_type === 'percent') {
                        this.bgInfo[group]['tag_label'] = d_val === 100 ? 'FREE' : d_val + '% OFF';
                        discount_amount = Math.round((d_val / 100) * bgSequence[seq - 1].price);
                    } else {
                        this.bgInfo[group]['tag_label'] = '₹' + d_val + 'OFF';
                        discount_amount = d_val;
                    }

                    bottomStrip += d_text;
                    bottomStrip = bottomStrip.replace('{{c_labels}}', this.selectedCustomization[group].label);
                    bottomStrip = bottomStrip.replace('{{discount_amount}}', discount_amount.toString());

                    this.bottomStrip = bottomStrip.split(':');
                    this.totalPrice -= discount_amount;
                }
            }
        }
    }

    isSelected(custTypeId, id) {
        const selectedProduct = this.selectedCustomization[this.activeGroupId].customization_types[custTypeId];
        return selectedProduct.id.indexOf(id) !== -1;
    }

    updateCustomization(filter, custId, custType) {
        switch (filter) {
            case 'Veg':
                this.filteredCustomizations[custId] = {
                    filter,
                    customizations: custType.customizations.filter((cust: any) => Boolean(cust.veg))
                };
                break;
            case 'Non-Veg':
                this.filteredCustomizations[custId] = {
                    filter,
                    customizations: custType.customizations.filter((cust: any) => !cust.veg)
                };
                break;
            default:
                this.filteredCustomizations[custId] = {
                    filter,
                    customizations: custType.customizations
                };
                break;
        }
    }

    filterChange(filter, custId) {
        this.selectedCustomization[this.activeGroupId].customization_types[custId].filter = filter;
        const custType = this.customizationList.get(this.activeGroupId).filter((cust: any) => cust.id === custId)[0];
        this.updateCustomization(filter, custId, custType);
    }

    checkReasonForInvalidity() {
        for (const cust in this.selectedCustomization[this.activeGroupId].customization_types) {
            const { min, id } = this.selectedCustomization[this.activeGroupId].customization_types[cust];
            if (min > id.length) {
                const customizationType = this.customizationList.get(this.activeGroupId).find(val => val.id === Number(cust));
                const customizationTypeLabel = customizationType.label;
                this.toastrService.openSnackBar(`Pick atleast ${customizationType.min} items from ${customizationTypeLabel}`, 'OKAY');
                return;
            }
        }
        return;
    }

    //submit the cart if all required customization is selected
    submit() {
        const { valid } = this.selectedCustomization[this.activeGroupId];

        if (!valid) {
            this.checkReasonForInvalidity();
            return;
        }

        for (const group of this.customizationGroupIds) {
            const { valid } = this.selectedCustomization[group];
            if (!valid) {
                this.checkReasonForInvalidity();
                this.onGroupChange(group);
                return;
            }
        }

        this.submitCart.emit(this.formatSelectionList());
        return;
    }

    //formats the selection for adding the selected customization in cart model
    formatSelectionList() {
        const list = {};
        for (const group in this.selectedCustomization) {
            for (const cust in this.selectedCustomization[group].customization_types) {
                if (this.selectedCustomization[group].customization_types[cust].id != null) {
                    list[cust] = this.selectedCustomization[group].customization_types[cust].id;
                }
            }
        }
        return list;
    }
}
